<template>
  <div class="rs-table-translations h-100">
    <div class="row m-0 align-items-center border-bottom">
      <div class="col font-weight-bold name-cell">
        <RsFormInput
          v-model="searchValue"
          type="text"
          :placeholder="$ts('tableTranslations.searchPlaceholder', { property: (translatePropertyDisplayName || translateProperty).toLowerCase() })"
          :debounce="300"
          icon="search"
        />
      </div>
    </div>

    <div class="row m-0 align-items-center border-bottom">
      <div class="col font-weight-bold name-cell">
        {{ translatePropertyDisplayName || $utils.capitalize(translateProperty) }}
      </div>
      <div
        v-for="(language, index) in languages"
        :key="index"
        class="col font-weight-bold"
      >
        {{ $ts('tableTranslations.languageHeader', { name: $ts(`languageNames.${language.languageCode}`, {}, language.name), code: language.languageCode }) }}
      </div>
    </div>

    <div
      v-if="itemsToEdit.length > 0 && !isLoading"
      class="rs-table-translations-body"
    >
      <OverlayScrollbars
        class="rs-scroll"
      >
        <div
          v-for="(item, itemIndex) in itemsToEdit"
          :key="itemIndex"
          class="row m-0 align-items-center border-bottom"
        >
          <div class="col name-cell">
            {{ item[translateProperty] }}
          </div>
          <div
            v-for="(language, languageIndex) in languages"
            :key="languageIndex"
            class="col"
          >
            <input
              v-model="(item.languages as unknown as { translations: { value: string }[] }[])[getLanguageIndex(language.id as number)].translations[0].value"
              type="text"
              class="form-control"
              :placeholder="$ts('tableTranslations.enterTranslation')"
            >
          </div>
        </div>
      </OverlayScrollbars>
    </div>

    <div
      v-else-if="isLoading"
      class="rs-table-translations-body mt-5"
    >
      <RsSpin />
    </div>

    <div
      v-else-if="!itemsToEdit.length && !isLoading"
      class="rs-table-translations-body"
    >
      <RsNoData :message="noDataMessage || $ts('dataTable.noResultsFound')" />
    </div>

    <div class="row m-0 align-items-center border-top">
      <div class="col text-right">
        <RsButton
          varian="primary"
          @click="handleSave()"
        >
          {{ $ts('tableTranslations.saveButton') }}
        </RsButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { ILanguages } from '@llws/lift-entity-interfaces';
import { cloneObject } from '@/shared';

export default defineComponent({
  name: 'RsTableTranslations',

  props: {
    items: {
      type: Array as PropType<Record<string, string>[]>,
      default: () => [],
    },

    translateProperty: {
      type: String,
      required: true,
    },

    translatePropertyDisplayName: {
      type: String,
      default: '',
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    noDataMessage: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      itemsToEdit: [] as Record<string, string>[],
      searchValue: '',
    };
  },

  computed: {
    languages(): ILanguages[] {
      return this.$rsGlobals.$siteSourceClientLanguages || [];
    },
  },

  watch: {
    items: {
      immediate: true,
      handler() {
        this.setItemsToEdit();
      },
    },

    searchValue(): void {
      this.setItemsToEdit();
    },
  },

  methods: {
    handleSave(): void {
      this.$emit('on-save', this.itemsToEdit);
    },

    getLanguageIndex(languageId: number): number {
      return this.languages.findIndex((l: ILanguages) => l.id === languageId);
    },

    setItemsToEdit(): void {
      this.itemsToEdit = cloneObject(this.items)
        .filter(
          (v: any) => v[this.translateProperty].toLowerCase().includes(this.searchValue.toLowerCase()),
        )
        .sort((a: any, b: any) => (
          a[this.translateProperty].toLowerCase() > b[this.translateProperty].toLowerCase() ? 1 : -1
        ))
        .map((v: any, i: number) => {
          const item = v;
          (item.languages || []).forEach((l: ILanguages, lIdx: number) => {
            if (l.translations![0]?.value) {
              item.languages[lIdx].translations[0].value = this.$utils.decodeHtml(item.languages[lIdx].translations[0].value);
            }
          });

          return item;
        });
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
$table-border: 1px solid rgba($tertiary-gray, .6);

.rs-table-translations {
  display: flex;
  flex-direction: column;
  width: 100%;
  border: $card-border;
  background: $white;
  border-radius: $global-radius;
  .name-cell {
    max-width: 250px;
  }
  .col {
    padding: 0.8rem 1.25rem;
  }
  .rs-table-translations-body {
    flex-grow: 1;
    height: 100%;
    .rs-scroll {
      overflow: auto;
      height: 100%;
    }
  }
  .rs-input {
    margin-bottom: 0 !important;
  }
}

</style>
